import React from 'react';
import { ScaleIcon, BarChart3Icon, ClipboardCheckIcon, EyeIcon, HandshakeIcon, BookOpenIcon, FlaskConicalIcon, AlertTriangleIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { FrameworkTimeline } from '../components/FrameworkTimeline';
import { AgreexaMark } from '../components/AgreexaMark';
const PRINCIPLES = [{
  icon: ScaleIcon,
  title: 'Editorial Principles',
  body: 'Independence, accuracy and reader benefit guide every decision. We publish what is true and useful, not what is profitable.'
}, {
  icon: BarChart3Icon,
  title: 'Scoring Methodology',
  body: 'Products are scored across seven weighted dimensions using a consistent, documented rubric to produce the Agreexa Trust Score®.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Review Standards',
  body: 'A review is only published after research, verification and senior editorial approval — never on a manufacturer’s timeline.'
}, {
  icon: EyeIcon,
  title: 'Transparency Policy',
  body: 'Our methodology, sources and reasoning are made visible so any reader can understand how a verdict was reached.'
}, {
  icon: HandshakeIcon,
  title: 'Affiliate Policy',
  body: 'Affiliate links may fund our work, but never influence scores or rankings. Commercial relationships are always disclosed.'
}, {
  icon: BookOpenIcon,
  title: 'Research Sources',
  body: 'We prioritise primary data, certified testing, standards bodies and verifiable field reports over secondary claims.'
}, {
  icon: FlaskConicalIcon,
  title: 'Testing Process',
  body: 'Where possible, products are assessed hands-on and measured against calibrated references and stated specifications.'
}, {
  icon: AlertTriangleIcon,
  title: 'Conflict of Interest Policy',
  body: 'Contributors declare any relationship that could bias a review. Conflicts are managed, disclosed or recused.'
}];
export function TrustFramework() {
  return <>
      {/* Intro */}
      <section className="w-full bg-white border-b border-stone-200" data-id="element-378">
        <div className="max-w-editorial mx-auto px-6 pt-16 pb-16 md:pt-24 md:pb-20" data-id="element-379">
          <div className="flex items-center gap-3 mb-8" data-id="element-380">
            <span className="eyebrow text-stone-500" data-id="element-381">
              The Standard Behind Every Review
            </span>
          </div>
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-382">
            <div className="lg:col-span-7" data-id="element-383">
              <h1 className="font-serif tracking-tightest text-ink text-4xl md:text-6xl leading-[1.02]" style={{
              fontWeight: 500
            }} data-id="element-384">
                The Agreexa Trust Framework®
              </h1>
              <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-2xl" data-id="element-385">
                A rigorous, transparent methodology that governs how every
                product on this publication is researched, evaluated and
                approved — the way UL certifies products or Consumer Reports
                evaluates them.
              </p>
            </div>
            <div className="lg:col-span-5" data-id="element-386">
              <div className="rounded-sm bg-ink text-paper p-8" data-id="element-387">
                <p className="eyebrow text-stone-400 mb-2" style={{
                fontSize: '0.6rem'
              }} data-id="element-388">
                  Maintained by
                </p>
                <AgreexaMark tone="light" showTagline className="text-2xl" data-id="element-389" />
                <p className="mt-5 text-sm text-stone-300 leading-relaxed" data-id="element-390">
                  Agreexa is an independent trust infrastructure. It does not
                  sell products — it builds the standards that make
                  recommendations trustworthy across every publication in its
                  ecosystem.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="w-full bg-paper" data-id="element-391">
        <div className="max-w-editorial mx-auto px-6 py-20 md:py-24" data-id="element-392">
          <p className="eyebrow text-trust-600 mb-4 text-center" data-id="element-393">
            The Six-Step Process
          </p>
          <h2 className="font-serif tracking-tightest text-ink text-3xl md:text-5xl text-center max-w-2xl mx-auto" style={{
          fontWeight: 500
        }} data-id="element-394">
            How a recommendation is made.
          </h2>
          <div className="mt-16 max-w-4xl mx-auto" data-id="element-395">
            <FrameworkTimeline data-id="element-396" />
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="w-full bg-white border-t border-stone-200" data-id="element-397">
        <div className="max-w-editorial mx-auto px-6 py-20 md:py-24" data-id="element-398">
          <PageHeaderInline data-id="element-399" />
          <div className="grid gap-px bg-stone-200 sm:grid-cols-2 rounded-sm overflow-hidden border border-stone-200" data-id="element-400">
            {PRINCIPLES.map(p => <div key={p.title} className="bg-white p-8" data-id="element-401">
                <p.icon className="h-7 w-7 text-trust-600" strokeWidth={1.5} data-id="element-402" />
                <h3 className="mt-5 font-serif text-xl tracking-tightest text-ink" style={{
              fontWeight: 600
            }} data-id="element-403">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed" data-id="element-404">
                  {p.body}
                </p>
              </div>)}
          </div>
        </div>
      </section>
    </>;
}
function PageHeaderInline() {
  return <div className="max-w-2xl mb-14" data-id="element-405">
      <p className="eyebrow text-trust-600 mb-4" data-id="element-406">Our Standards</p>
      <h2 className="font-serif tracking-tightest text-ink text-3xl md:text-4xl leading-[1.05]" style={{
      fontWeight: 500
    }} data-id="element-407">
        Eight commitments that keep us accountable.
      </h2>
    </div>;
}